import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, ScanSearch } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const ARTIFACTS = ["terraform", "dockerfile", "github-actions-yaml", "bash", "logs", "iam-policy", "security-group", "architecture"];

export default function Practice() {
  const [exercises, setExercises] = useState([]);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [artifact, setArtifact] = useState("terraform");
  const [content, setContent] = useState("");
  const [review, setReview] = useState("");

  const load = () => api.get("/exercises").then(({ data }) => setExercises(data.exercises)).catch(() => setExercises([]));
  useEffect(() => {
    load();
  }, []);

  const submit = async (slug) => {
    setBusy(slug);
    setError("");
    try {
      await api.post(`/exercises/${slug}/submit`, { content: answers[slug] || "" });
      await load();
    } catch (e) {
      setError(errMsg(e, "Unable to submit"));
    } finally {
      setBusy("");
    }
  };

  const runReview = async () => {
    setBusy("review");
    setError("");
    setReview("");
    try {
      const { data } = await api.post("/ai/review", { artifactType: artifact, content });
      setReview(data.review);
    } catch (e) {
      setError(errMsg(e, "AI review unavailable"));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-12 fade-up" data-testid="practice-page">
      <div>
        <p className="overline text-primary">practice</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-black tracking-tighter">Write it, then get it reviewed</h1>
        <p className="mt-3 text-sm text-muted-foreground">{exercises.length} exercises. Exercises complete only after you submit an answer.</p>
        <Link to="/labs" className="mt-4 inline-block font-mono text-xs text-primary hover:underline" data-testid="practice-labs-link">
          → troubleshooting labs (scored incidents)
        </Link>
      </div>

      {error && <p data-testid="practice-error" className="text-sm text-destructive">{error}</p>}

      <section className="border border-border" data-testid="ai-review-section">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-border">
          <ScanSearch className="h-4 w-4 text-primary" />
          <p className="font-display font-bold tracking-tight">AI code &amp; config review</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap gap-1.5">
            {ARTIFACTS.map((a) => (
              <button
                key={a}
                data-testid={`artifact-${a}`}
                onClick={() => setArtifact(a)}
                className={`px-3 py-1.5 font-mono text-xs border transition-colors duration-150 ${
                  artifact === a ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
          <Textarea
            data-testid="review-input"
            rows={10}
            className="rounded-none font-mono text-xs"
            placeholder={"resource \"aws_s3_bucket\" \"assets\" {\n  bucket = \"my-bucket\"\n  acl    = \"public-read\"\n}"}
            value={content}
            onChange={(e) => setContent(e.target.value)}
          />
          <p className="font-mono text-xs text-muted-foreground">Secrets are redacted server-side before anything reaches the model.</p>
          <Button className="rounded-none gap-2" onClick={runReview} disabled={busy === "review" || content.trim().length < 10} data-testid="review-submit">
            {busy === "review" && <Loader2 className="h-4 w-4 animate-spin" />} Run review
          </Button>
          {review && (
            <div data-testid="review-output" className="border border-border bg-accent/40 p-4 text-sm whitespace-pre-wrap leading-relaxed font-mono">
              {review}
            </div>
          )}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="font-display text-xl font-bold tracking-tight">Exercises</h2>
        {exercises.map((ex) => (
          <div key={ex.slug} data-testid={`practice-exercise-${ex.slug}`} className="border border-border p-5">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <p className="font-display font-bold tracking-tight">{ex.title}</p>
              <span className="font-mono text-xs text-muted-foreground">{ex.moduleSlug} · {ex.submissionType}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{ex.prompt}</p>
            <Textarea
              data-testid={`practice-input-${ex.slug}`}
              rows={4}
              className="mt-4 rounded-none font-mono text-xs"
              placeholder="Your solution…"
              value={answers[ex.slug] || ""}
              onChange={(e) => setAnswers({ ...answers, [ex.slug]: e.target.value })}
            />
            <div className="mt-3 flex items-center gap-3">
              <Button
                size="sm"
                className="rounded-none gap-2"
                disabled={busy === ex.slug || (answers[ex.slug] || "").trim().length < 10}
                onClick={() => submit(ex.slug)}
                data-testid={`practice-submit-${ex.slug}`}
              >
                {busy === ex.slug && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Submit
              </Button>
              {ex.submitted && (
                <span data-testid={`practice-done-${ex.slug}`} className="font-mono text-xs text-[hsl(var(--success))] flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> submitted
                </span>
              )}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
